import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const kvCacheQuantizationComponent: ComponentDef = {
  id: 'kv-cache-quantization',
  name: 'KV Cache Quantization',
  description: 'Quantize KV cache entries to INT4/INT8 to reduce memory bandwidth: KIVI (per-channel INT2) and KVQuant (outlier-aware INT4).',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Cache sequence length', type: 'number', default: 1024, min: 64, max: 32768, step: 64 },
    {
      name: 'variant',
      label: 'Quantization method',
      type: 'select',
      default: 'kivi',
      options: [
        { label: 'KIVI (per-channel INT2/INT4)', value: 'kivi' },
        { label: 'KVQuant (outlier-aware INT4)', value: 'kvquant' }
      ]
    },
    { name: 'bits', label: 'Bits', type: 'number', default: 4, min: 2, max: 8, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const seq_len = Number(params.seq_len);
    const bits = Number(params.bits);
    const d_head = d_model / n_heads;
    const variant = String(params.variant || 'kivi');

    const g = new GraphBuilder();
    g.input('q', [n_heads, 1, d_head]);
    g.input('k_fp16', [n_heads, seq_len, d_head]);
    g.input('v_fp16', [n_heads, seq_len, d_head]);

    if (variant === 'kivi') {
      // KIVI: per-channel group quantization; separate residual buffer for recent tokens
      const residual_len = 32;
      const hist_len = seq_len - residual_len;

      // Historical KV: quantize to INT2/INT4 per channel group
      g.reshape('k_hist', 'k_fp16', [n_heads, hist_len, d_head]);
      g.activation('k_scale', 'k_hist', 'per-channel group scale');
      g.quantize('k_quant', 'k_hist', [n_heads, hist_len, d_head], bits);

      g.reshape('v_hist', 'v_fp16', [n_heads, hist_len, d_head]);
      g.activation('v_scale', 'v_hist', 'per-channel group scale');
      g.quantize('v_quant', 'v_hist', [n_heads, hist_len, d_head], bits);

      // Recent residual buffer stays in FP16 (avoids quantization error on new tokens)
      g.reshape('k_resid', 'k_fp16', [n_heads, residual_len, d_head]);
      g.reshape('v_resid', 'v_fp16', [n_heads, residual_len, d_head]);

      // Dequantize for attention compute
      g.dequantize('k_dq', 'k_quant', [n_heads, hist_len, d_head]);
      g.dequantize('v_dq', 'v_quant', [n_heads, hist_len, d_head]);

      // Concat dequantized + residual
      g.concat('k_full', ['k_dq', 'k_resid'], [n_heads, seq_len, d_head]);
      g.concat('v_full', ['v_dq', 'v_resid'], [n_heads, seq_len, d_head]);

    } else {
      // KVQuant: identify per-head outlier channels; quantize non-outliers to INT4, outliers to FP16
      g.activation('outlier_mask', 'k_fp16', 'Per-head outlier channels (|k| > threshold)');
      g.scale('k_non_outlier', 'k_fp16', 'Non-outlier channels');
      g.scale('k_outlier', 'k_fp16', 'Outlier channels (kept FP16)');

      g.quantize('k_quant', 'k_non_outlier', [n_heads, seq_len, d_head], bits);
      g.dequantize('k_dq', 'k_quant', [n_heads, seq_len, d_head]);

      // Merge: non-outlier dequantized + outlier FP16
      g.add('k_full', 'k_dq', 'k_outlier');

      // V quantization (uniform INT4 with per-token scaling)
      g.activation('v_scale', 'v_fp16', 'per-token scale');
      g.quantize('v_quant', 'v_fp16', [n_heads, seq_len, d_head], bits);
      g.dequantize('v_full', 'v_quant', [n_heads, seq_len, d_head]);
    }

    // Attention with quantized KV cache
    g.transpose('kt', variant === 'kivi' ? 'k_full' : 'k_full', [n_heads, d_head, seq_len], 'K^T');
    g.matmul('scores', 'q', 'kt', [n_heads, 1, seq_len]);
    g.scale('scaled', 'scores', '1/√d_k');
    g.softmax('attn_w', 'scaled', -1);
    g.matmul('out', 'attn_w', variant === 'kivi' ? 'v_full' : 'v_full', [n_heads, 1, d_head]);

    g.output('out', 'out');
    return g.build();
  }
};
